// apps/admin/src/queries/adminRoleKeys.ts
//
// Phase 8.2 Wave 7 Task 51 — TanStack Query key factory for the Admin
// Roles CRUD surface + invalidation helpers.
//
// Per CONTEXT.md
//   • Role CRUD with optimistic update + invalidation. Every
//                mutation hook MUST invalidate `adminRoleKeys.lists()` so
//                the list re-renders with fresh member counts + perm
//                counts.
//   • Permission catalog cache key (separate scope so a role
//                edit doesn't refetch the immutable catalog).
//
// Convention (TanStack Query v5 readonly tuple keys):
//   ['admin','roles']                         — all roles slices (mutation invalidate)
//   ['admin','roles','list', filters]         — specific list filter
//   ['admin','roles','detail', roleId]        — single role editor fetch
//   ['admin','permissions','catalog']         — flat colon-key catalog (5 min stale)
//
// Pages MUST import keys from this factory rather than hand-rolling tuples
// at call sites — keeps invalidation consistent.

import type { QueryClient } from '@tanstack/react-query';

// ---------------------------------------------------------------------------
// Filter shape for the list endpoint
// ---------------------------------------------------------------------------

export interface AdminRoleListFilters {
  /** Optional `type` filter (`built_in` | `custom` | `all`). Defaults to `all`. */
  type?: 'built_in' | 'custom' | 'all';
}

// ---------------------------------------------------------------------------
// Key factory
// ---------------------------------------------------------------------------

export const adminRoleKeys = {
  /** Whole roles scope — invalidate after a destructive cross-role op. */
  all: () => ['admin', 'roles'] as const,
  /** All list slices — invalidate after every CRUD mutation. */
  lists: () => ['admin', 'roles', 'list'] as const,
  /** Specific list filter — used by AdminRolesPage. */
  list: (filters: AdminRoleListFilters = {}) =>
    ['admin', 'roles', 'list', filters] as const,
  /** Detail fetch for editor — keyed by Mongo ObjectId. */
  detail: (roleId: string) =>
    ['admin', 'roles', 'detail', roleId] as const,
  /**
   * Permission catalog (immutable per build). Kept separate so role
   * edits do not refetch the catalog.
   */
  catalog: () => ['admin', 'permissions', 'catalog'] as const,
} as const;

// ---------------------------------------------------------------------------
// Invalidation helpers
// ---------------------------------------------------------------------------

/**
 * Invalidate every Admin Roles list slice + the specific detail slice (when
 * provided). Used by create / update / delete mutation hooks to keep the
 * page cache consistent optimistic-update contract.
 */
export function invalidateAdminRoleCaches(
  qc: QueryClient,
  roleId?: string,
): void {
  void qc.invalidateQueries({ queryKey: adminRoleKeys.lists() });
  if (roleId) {
    void qc.invalidateQueries({ queryKey: adminRoleKeys.detail(roleId) });
  }
}
